/**
 * Token storage.
 *
 * sessionStorage rather than localStorage on purpose: a booth kiosk is a shared
 * device, and an admin session should not survive the browser closing on one.
 * The refresh token means a working day never needs a re-login anyway.
 */

const ACCESS_KEY = "jeba.access_token";
const REFRESH_KEY = "jeba.refresh_token";
const ORG_KEY = "jeba.organization_id";

type Listener = () => void;
const listeners = new Set<Listener>();

function read(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null; // Private mode, or storage disabled on a locked-down kiosk.
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    /* Non-fatal: the session simply won't survive a reload. */
  }
}

export const tokenStore = {
  get accessToken() { return read(ACCESS_KEY); },
  get refreshToken() { return read(REFRESH_KEY); },
  get organizationId() { return read(ORG_KEY); },

  set(accessToken: string, refreshToken?: string) {
    write(ACCESS_KEY, accessToken);
    if (refreshToken) write(REFRESH_KEY, refreshToken);
    listeners.forEach((listener) => listener());
  },

  /** Superadmin acting on behalf of another organization. */
  setOrganizationId(id: number | string | null) {
    write(ORG_KEY, id === null ? null : String(id));
    listeners.forEach((listener) => listener());
  },

  clear() {
    write(ACCESS_KEY, null);
    write(REFRESH_KEY, null);
    write(ORG_KEY, null);
    listeners.forEach((listener) => listener());
  },

  subscribe(listener: Listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};
